import { useState, type FormEvent } from "react";
import { ApiError, api } from "../lib/api.ts";
import { useApi } from "../lib/hooks.ts";
import { useAuth } from "../lib/auth.tsx";
import type { DeviceGroup, PolicyGroup } from "../lib/types.ts";
import { Card, Empty, ErrorAlert, Modal, Spinner } from "../components/ui.tsx";

export function GroupsPage() {
  const { can } = useAuth();
  const groups = useApi<{ data: DeviceGroup[] }>("/api/v3/device-groups");
  const policies = useApi<{ data: PolicyGroup[] }>("/api/v3/policy-groups");

  const [openGroup, setOpenGroup] = useState(false);
  const [openPolicy, setOpenPolicy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [agentVersion, setAgentVersion] = useState("11.2.0");

  function reset() {
    setName("");
    setDescription("");
    setAgentVersion("11.2.0");
    setError(null);
  }

  async function createGroup(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/v3/device-groups", { method: "POST", body: { name, description } });
      setOpenGroup(false);
      reset();
      groups.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao criar grupo");
    } finally {
      setBusy(false);
    }
  }

  async function createPolicy(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/v3/policy-groups", {
        method: "POST",
        body: { name, description, agentVersion },
      });
      setOpenPolicy(false);
      reset();
      policies.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao criar policy group");
    } finally {
      setBusy(false);
    }
  }

  const writable = can("groups.write");

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Grupos e politicas</h1>
          <p className="subtle">
            Agrupamento de dispositivos e policy groups com a versao de agente associada.
          </p>
        </div>
      </div>

      <Card
        title="Grupos de dispositivos"
        action={
          <button className="btn primary sm" disabled={!writable} onClick={() => { reset(); setOpenGroup(true); }}>
            Novo grupo
          </button>
        }
        tight
      >
        {groups.loading && !groups.data ? (
          <div className="empty"><Spinner /></div>
        ) : !groups.data?.data.length ? (
          <Empty>Nenhum grupo cadastrado.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Grupo</th>
                  <th>Descricao</th>
                  <th>Dispositivos</th>
                </tr>
              </thead>
              <tbody>
                {groups.data.data.map((g) => (
                  <tr key={g.id}>
                    <td className="cell-title">{g.name}</td>
                    <td className="cell-sub">{g.description || "—"}</td>
                    <td>{g.deviceCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Policy groups"
        action={
          <button className="btn primary sm" disabled={!writable} onClick={() => { reset(); setOpenPolicy(true); }}>
            Novo policy group
          </button>
        }
        tight
      >
        {policies.loading && !policies.data ? (
          <div className="empty"><Spinner /></div>
        ) : !policies.data?.data.length ? (
          <Empty>Nenhum policy group cadastrado.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Versao do agente</th>
                  <th>Descricao</th>
                </tr>
              </thead>
              <tbody>
                {policies.data.data.map((p) => (
                  <tr key={p.id}>
                    <td className="cell-title">{p.name}</td>
                    <td className="mono">{p.agentVersion}</td>
                    <td className="cell-sub">{p.description || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openGroup ? (
        <Modal
          title="Novo grupo de dispositivos"
          onClose={() => setOpenGroup(false)}
          footer={
            <>
              <button className="btn" onClick={() => setOpenGroup(false)} disabled={busy}>
                Cancelar
              </button>
              <button className="btn primary" form="group-form" disabled={busy || !name.trim()}>
                Criar grupo
              </button>
            </>
          }
        >
          {error ? <ErrorAlert message={error} /> : null}
          <form id="group-form" onSubmit={createGroup} style={{ display: "grid", gap: 12 }}>
            <div className="field">
              <label htmlFor="g-name">Nome</label>
              <input id="g-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="g-desc">Descricao</label>
              <input id="g-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </form>
        </Modal>
      ) : null}

      {openPolicy ? (
        <Modal
          title="Novo policy group"
          onClose={() => setOpenPolicy(false)}
          footer={
            <>
              <button className="btn" onClick={() => setOpenPolicy(false)} disabled={busy}>
                Cancelar
              </button>
              <button className="btn primary" form="policy-form" disabled={busy || !name.trim()}>
                Criar
              </button>
            </>
          }
        >
          {error ? <ErrorAlert message={error} /> : null}
          <form id="policy-form" onSubmit={createPolicy} style={{ display: "grid", gap: 12 }}>
            <div className="field">
              <label htmlFor="p-name">Nome</label>
              <input id="p-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="p-agent">Versao do agente</label>
              <select id="p-agent" className="select" value={agentVersion} onChange={(e) => setAgentVersion(e.target.value)}>
                <option value="11.3.0-beta">11.3.0-beta</option>
                <option value="11.2.0">11.2.0</option>
                <option value="11.1.4">11.1.4</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="p-desc">Descricao</label>
              <input id="p-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
